import {
  ChangeEvent,
  MouseEvent,
  memo,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Typography } from "@mui/material";
import { toast } from "react-toastify";
import {
  Box,
  ConfirmationDialog,
  FlexColBox,
  Select,
  Table,
  TableColumn,
} from "../../common/components";
import { SortBy } from "../../common/components/Table/Table";
import { useSelector } from "../../common/hooks/useSelector";
import AdminUserService from "../../service/AdminUserService";
import { ROLES_ENUM } from "../../service/authService";
import { spacing } from "../../styles/theme";
import { ManagedUser, UserInfo } from "../../types";
import { useMoment } from "../../common/hooks/useMoment";

type PendingRoleUpdate = {
  userId: string;
  userName: string;
  role: ROLES_ENUM;
};

type PendingPriceTierUpdate = {
  userId: string;
  userName: string;
  priceTier: ManagedUser["priceTier"];
};

const ROLE_OPTIONS = [
  {
    label: "User",
    value: ROLES_ENUM.USER,
  },
  {
    label: "Designer",
    value: ROLES_ENUM.DESIGNER,
  },
  {
    label: "Admin",
    value: ROLES_ENUM.ADMIN,
  },
  {
    label: "Distributor",
    value: ROLES_ENUM.DISTRIBUTOR,
    disabled: true,
  },
];

const PRICE_TIER_OPTIONS = [
  {
    label: "None",
    value: "",
  },
  {
    label: "A1",
    value: "A1",
  },
  {
    label: "A2",
    value: "A2",
  },
  {
    label: "A3",
    value: "A3",
  },
  {
    label: "C",
    value: "C",
  },
];

const AdminUserRoleList = () => {
  const { formatDateTime } = useMoment();
  const currentUser = useSelector((state) => state.userInfo) as UserInfo;
  const [isRefetching, setIsRefetching] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<SortBy | undefined>({
    columnId: "createdAt",
    direction: "desc",
  });
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [pendingRoleUpdate, setPendingRoleUpdate] =
    useState<PendingRoleUpdate>();
  const [pendingPriceTierUpdate, setPendingPriceTierUpdate] =
    useState<PendingPriceTierUpdate>();
  const [updatingUserId, setUpdatingUserId] = useState<string>();

  const refetch = useCallback(() => {
    setIsRefetching(true);
    AdminUserService.getUsers(page, rowsPerPage, searchTerm, sortBy)
      .then((res) => {
        setUsers(res.users || []);
        setTotalCount(res.count);
      })
      .catch(() => {
        toast.error("Unable to load users");
      })
      .finally(() => {
        setIsRefetching(false);
        setIsInitialLoading(false);
      });
  }, [page, rowsPerPage, searchTerm, sortBy]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleChangePage = (
    _: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = Number.isNaN(Number(event.target.value))
      ? 50
      : Number(event.target.value);
    setRowsPerPage(newValue);
    setPage(0);
  };

  const onConfirmRoleUpdate = async () => {
    if (!pendingRoleUpdate) {
      return;
    }

    setUpdatingUserId(pendingRoleUpdate.userId);
    try {
      const updatedUser = await AdminUserService.updateUserRole(
        pendingRoleUpdate.userId,
        pendingRoleUpdate.role
      );
      setUsers((currentUsers) =>
        currentUsers.map((user) =>
          user.id === updatedUser.id ? { ...user, ...updatedUser } : user
        )
      );
      toast.success("User role updated");
      setPendingRoleUpdate(undefined);
    } catch (_error) {
      toast.error("Unable to update user role");
    } finally {
      setUpdatingUserId(undefined);
    }
  };

  const onConfirmPriceTierUpdate = async () => {
    if (!pendingPriceTierUpdate) {
      return;
    }

    setUpdatingUserId(pendingPriceTierUpdate.userId);
    try {
      const updatedUser = await AdminUserService.updateUserPriceTier(
        pendingPriceTierUpdate.userId,
        pendingPriceTierUpdate.priceTier
      );
      setUsers((currentUsers) =>
        currentUsers.map((user) =>
          user.id === updatedUser.id ? { ...user, ...updatedUser } : user
        )
      );
      toast.success("User pricing tier updated");
      setPendingPriceTierUpdate(undefined);
    } catch (_error) {
      toast.error("Unable to update user pricing tier");
    } finally {
      setUpdatingUserId(undefined);
    }
  };

  const columns: TableColumn<ManagedUser>[] = [
    {
      id: "firstName",
      labelKey: "distributors.table.name",
      sortable: true,
      customSortKey: "name",
      width: "22%",
      render: (row) => (
        <Typography variant="textRegular">{`${row.firstName} ${row.lastName}`}</Typography>
      ),
    },
    {
      id: "email",
      labelKey: "distributors.table.email",
      sortable: true,
      width: "30%",
    },
    {
      id: "createdAt",
      labelKey: "distributors.table.createdAt",
      sortable: true,
      format: (value) => (value ? formatDateTime(value) : ""),
    },
    {
      id: "role",
      labelKey: "Role",
      width: "220px",
      render: (row) => {
        return (
          <Select
            controlled
            value={row.role}
            options={ROLE_OPTIONS}
            disabled={row.id === currentUser.id || updatingUserId === row.id}
            onChange={(event) => {
              const nextRole = event.target.value as ROLES_ENUM;
              if (nextRole === row.role) {
                return;
              }
              setPendingRoleUpdate({
                userId: row.id,
                userName: `${row.firstName} ${row.lastName}`,
                role: nextRole,
              });
            }}
            noMaxWidth
            size="small"
          />
        );
      },
    },
    {
      id: "priceTier",
      labelKey: "Pricing tier",
      width: "180px",
      render: (row) => {
        return (
          <Select
            controlled
            value={row.priceTier ?? ""}
            options={PRICE_TIER_OPTIONS}
            disabled={updatingUserId === row.id}
            onChange={(event) => {
              const value = event.target.value;
              const nextPriceTier =
                value === "" ? null : (value as ManagedUser["priceTier"]);
              if (nextPriceTier === row.priceTier) {
                return;
              }
              setPendingPriceTierUpdate({
                userId: row.id,
                userName: `${row.firstName} ${row.lastName}`,
                priceTier: nextPriceTier,
              });
            }}
            noMaxWidth
            size="small"
          />
        );
      },
    },
  ];

  return (
    <FlexColBox
      p={{
        xs: `${spacing.regularMobile} ${spacing.largeMobile} ${spacing.xlMobile}`,
        lg: `${spacing.regularDesktop} ${spacing.largeDesktop} ${spacing.xlDesktop}`,
      }}
      gap={{ xs: spacing.groupingMobile, lg: spacing.groupingDesktop }}
      fullWidth
    >
      <Box display="flex" flexDirection="column" gap="8px">
        <Typography variant="title" fontWeight="medium">
          User roles
        </Typography>
        <Typography variant="textRegular" color="text.secondary">
          Grant or remove admin and designer access for Studio users.
        </Typography>
      </Box>
      <Table
        isInitialLoading={isInitialLoading}
        isRefetching={isRefetching}
        columns={columns}
        data={users}
        rowsPerPage={rowsPerPage}
        page={page}
        sortBy={sortBy}
        totalCount={totalCount}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleSearch={setSearchTerm}
        handleSort={setSortBy}
        containerSx={{
          minHeight: "0",
        }}
      />
      <ConfirmationDialog
        open={Boolean(pendingRoleUpdate)}
        onClose={() => setPendingRoleUpdate(undefined)}
        onConfirm={onConfirmRoleUpdate}
        title="Update user role?"
        description={`Set ${pendingRoleUpdate?.userName} to ${pendingRoleUpdate?.role}?`}
      />
      <ConfirmationDialog
        open={Boolean(pendingPriceTierUpdate)}
        onClose={() => setPendingPriceTierUpdate(undefined)}
        onConfirm={onConfirmPriceTierUpdate}
        title="Update pricing tier?"
        description={`Set ${pendingPriceTierUpdate?.userName} pricing tier to ${
          pendingPriceTierUpdate?.priceTier ?? "none"
        }?`}
      />
    </FlexColBox>
  );
};

export default memo(AdminUserRoleList);
